/**
 * DepartmentScopePicker — the ONE control for "which departments is this
 * person in?", on the Users rail.
 *
 * Foundations 2 (Users rail), B2b-T3. A person belongs to one or more
 * departments, or to all of them (decisions.md, "Departments scope what people
 * see"), so the single `<Select>` both the create wizard and the edit dialog
 * carried can no longer say what an admin means. This is that same field —
 * same row, same height, same label above it — with a list behind it.
 *
 * WHY ONE COMPONENT AND NOT TWO EDITS. The wizard and the edit dialog are the
 * two places a person's departments are set, and they have to agree about three
 * things a `<Select>` never had to express: what "All departments" means, that
 * ticking it clears the list, and that "not all departments, and none picked"
 * is a scope showing nothing. Two hand-rolled checkbox lists would be two
 * chances for one of them to send `{ all: false, ids: [] }` and collect the
 * server's 400 — or worse, to coerce it to "all" and widen somebody silently.
 *
 * THE TRANSIENT EMPTY STATE IS ALLOWED HERE AND REFUSED AT SAVE. Un-ticking
 * the last department leaves `{ all: false, ids: [] }` on screen with the
 * reason printed under the control, because the alternative — snapping back to
 * "All departments" — is the widening the admin did not ask for, made
 * invisible. `isCompleteScope()` below is what the dialogs gate Save on; it is
 * the same sentence the server's `UserDepartmentsWriteSchema` refuses with.
 *
 * NO NEW NAVIGATION, NO NEW PAGE, NO LAYOUT CHANGE: it renders inside the
 * existing Department field of both dialogs.
 */

import { useMemo, useState } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * What an admin has asked for — the wire shape of `departments` on POST /users
 * and PATCH /users/:id (packages/shared/src/schemas/departments.ts,
 * `UserDepartmentsWriteSchema`).
 */
export interface DepartmentScopeValue {
  /** Every department in the tenant, including ones created tomorrow. */
  all: boolean;
  /** The departments they are in. Ignored by the server when `all` is true. */
  ids: string[];
}

/**
 * Is this a scope the server will accept?
 *
 * `{ all: false, ids: [] }` is a person who shares a department with nothing:
 * they would see company-wide items and nothing else, which is never what
 * picking "not all departments" was meant to say. The server answers it with a
 * 400; the dialogs disable Save on it so the admin sees the reason before the
 * round trip rather than as a toast afterwards.
 */
export function isCompleteScope(value: DepartmentScopeValue): boolean {
  return value.all || value.ids.length > 0;
}

/** The sentence shown when a scope is incomplete. Same rule, one wording. */
export const INCOMPLETE_SCOPE_MESSAGE =
  'Pick at least one department, or choose “All departments”.';

/**
 * The scope as the server just reported it, turned into what the control edits.
 *
 * Loaded from the user's `departments` object — `{ all, items }` — so a dialog
 * opens on the store's answer and not on a local reconstruction of it.
 */
export function scopeFromUserDepartments(
  departments: { all: boolean; items: { id: string; name: string }[] } | null | undefined,
): DepartmentScopeValue {
  if (!departments) return { all: false, ids: [] };
  return {
    all: departments.all === true,
    ids: departments.all ? [] : departments.items.map((d) => d.id),
  };
}

/** Are two scopes the same? The dirty check both dialogs use. */
export function sameScope(a: DepartmentScopeValue, b: DepartmentScopeValue): boolean {
  if (a.all !== b.all) return false;
  if (a.all) return true;
  const x = [...new Set(a.ids)].sort();
  const y = [...new Set(b.ids)].sort();
  return x.length === y.length && x.every((id, i) => id === y[i]);
}

export interface DepartmentScopePickerProps {
  value: DepartmentScopeValue;
  onChange: (next: DepartmentScopeValue) => void;
  /** The tenant's departments, from `useDepartments()`. */
  departments: { id: string; name: string }[];
  disabled?: boolean;
  /**
   * Shown when the tenant has no departments configured yet — the wizard and
   * the edit dialog already say slightly different things there, so the words
   * stay with the caller.
   */
  emptyHint?: string;
  'aria-label'?: string;
}

export function DepartmentScopePicker({
  value,
  onChange,
  departments,
  disabled,
  emptyHint = 'No departments configured for this tenant.',
  'aria-label': ariaLabel = 'Departments',
}: DepartmentScopePickerProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => new Set(value.ids), [value.ids]);

  /** The trigger's text: the same three states the roster cell renders. */
  const label = value.all
    ? 'All departments'
    : value.ids.length === 0
      ? '— No department —'
      : departments
          .filter((d) => selected.has(d.id))
          .map((d) => d.name)
          .join(', ') || `${value.ids.length} selected`;

  const toggle = (id: string) => {
    // Ticking a department off "all" starts a NARROWED scope holding just it:
    // an all-departments person has no membership rows, so there is no list to
    // add to (decisions.md C7).
    if (value.all) {
      onChange({ all: false, ids: [id] });
      return;
    }
    const next = new Set(value.ids);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ all: false, ids: [...next] });
  };

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            disabled={disabled}
            className="h-9 w-full justify-between text-xs font-normal"
          >
            <span className="flex items-center gap-1.5 truncate">
              <Building2 className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{label}</span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-1" align="start">
          <div className="max-h-56 overflow-y-auto">
            <label
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
              data-testid="department-scope-all"
            >
              <Checkbox
                checked={value.all}
                onCheckedChange={(checked) =>
                  // Ticking "all" clears the list, because the two halves are
                  // one fact: an all-departments person holds no membership
                  // rows. Un-ticking it leaves an EMPTY narrowed scope on
                  // purpose — see the header.
                  onChange(checked === true ? { all: true, ids: [] } : { all: false, ids: [] })
                }
                aria-label="All departments"
              />
              <span className="font-medium">All departments</span>
              {value.all && <Check className="ml-auto h-3 w-3 text-muted-foreground" />}
            </label>

            {departments.length === 0 ? (
              <div className="px-2 py-1.5 text-[11px] text-muted-foreground">{emptyHint}</div>
            ) : (
              departments.map((d) => (
                <label
                  key={d.id}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
                >
                  <Checkbox
                    checked={!value.all && selected.has(d.id)}
                    onCheckedChange={() => toggle(d.id)}
                    aria-label={d.name}
                  />
                  <span className={cn('truncate', value.all && 'text-muted-foreground')}>
                    {d.name}
                  </span>
                </label>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {!isCompleteScope(value) && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          {INCOMPLETE_SCOPE_MESSAGE}
        </p>
      )}
    </div>
  );
}
